import { findAll, prefersReducedMotion } from './dom.js';

const TIMELINE_SELECTOR = '[data-bp-timeline]';
const YEAR_SELECTOR = '.ht-tl-year';
const PANE_SELECTOR = '.ht-tl-pane';
const NAV_SELECTOR = '[data-bp-tl-nav]';
const STRIP_SELECTOR = '.ht-tl-strip';
const TRACK_SELECTOR = '.ht-tl-track';
const READY_ATTRIBUTE = 'data-bp-ready';
const READY_VALUE = 'timeline';
const ACTIVE_CLASS = 'is-active';

let instances = 0;

export function clampIndex(index, count) {
  return Math.min(Math.max(index, 0), count - 1);
}

// Arrow keys step, Home/End jump; anything else is not ours.
export function keyTarget(key, index, count) {
  if (key === 'ArrowRight' || key === 'ArrowDown') return clampIndex(index + 1, count);
  if (key === 'ArrowLeft' || key === 'ArrowUp') return clampIndex(index - 1, count);
  if (key === 'Home') return 0;
  if (key === 'End') return count - 1;
  return null;
}

function initializeTimeline(root) {
  const years = findAll(root, YEAR_SELECTOR);
  const panes = findAll(root, PANE_SELECTOR);
  const strip = root.querySelector(STRIP_SELECTOR);
  const track = root.querySelector(TRACK_SELECTOR);
  const count = Math.min(years.length, panes.length);
  if (count < 2 || !strip || !track) return false;

  const view = root.ownerDocument?.defaultView ?? globalThis.window;
  const prefix = `bp-tl-${(instances += 1)}`;
  let index = 0;

  const prev = [];
  const next = [];
  for (const button of findAll(root, NAV_SELECTOR)) {
    (button.getAttribute('data-bp-tl-nav') === 'next' ? next : prev).push(button);
    button.setAttribute('type', 'button');
  }

  strip.setAttribute('role', 'tablist');
  strip.setAttribute('aria-orientation', 'horizontal');
  for (let i = 0; i < count; i += 1) {
    years[i].setAttribute('type', 'button');
    years[i].setAttribute('role', 'tab');
    years[i].setAttribute('id', `${prefix}-tab-${i}`);
    years[i].setAttribute('aria-controls', `${prefix}-pane-${i}`);
    panes[i].setAttribute('role', 'tabpanel');
    panes[i].setAttribute('id', `${prefix}-pane-${i}`);
    panes[i].setAttribute('aria-labelledby', `${prefix}-tab-${i}`);
    panes[i].setAttribute('tabindex', '0');
  }

  // The active year's centre sits on the strip's centre. Placement without motion (load, resize, reduced motion) skips the transition.
  const place = (animate) => {
    const year = years[index];
    const offset = strip.clientWidth / 2 - (year.offsetLeft + year.offsetWidth / 2);
    const still = !animate || prefersReducedMotion(view);
    if (still) track.style.transition = 'none';
    track.style.transform = `translateX(${Math.round(offset)}px)`;
    if (still) {
      void track.offsetWidth;
      track.style.transition = '';
    }
  };

  const setActive = (target, { animate = true, focus = false } = {}) => {
    index = clampIndex(target, count);
    for (let i = 0; i < count; i += 1) {
      const selected = i === index;
      years[i].classList[selected ? 'add' : 'remove'](ACTIVE_CLASS);
      years[i].setAttribute('aria-selected', String(selected));
      years[i].setAttribute('tabindex', selected ? '0' : '-1');
      if (selected) panes[i].removeAttribute('hidden');
      else panes[i].setAttribute('hidden', '');
    }
    for (const button of prev) button.disabled = index === 0;
    for (const button of next) button.disabled = index === count - 1;
    place(animate);
    if (focus) years[index].focus({ preventScroll: true });
  };

  years.slice(0, count).forEach((year, i) => {
    year.addEventListener('click', () => setActive(i));
  });
  for (const button of prev) button.addEventListener('click', () => setActive(index - 1));
  for (const button of next) button.addEventListener('click', () => setActive(index + 1));
  strip.addEventListener('keydown', (event) => {
    const target = keyTarget(event.key, index, count);
    if (target === null) return;
    event.preventDefault();
    setActive(target, { focus: true });
  });

  if (typeof view?.ResizeObserver === 'function') {
    const observer = new view.ResizeObserver(() => place(false));
    observer.observe(strip);
    observer.observe(track);
  } else {
    view?.addEventListener?.('resize', () => place(false));
  }

  setActive(0, { animate: false });
  root.setAttribute(READY_ATTRIBUTE, READY_VALUE);
  return true;
}

export function initializeTimelines(root) {
  let initialized = 0;
  for (const timeline of findAll(root, TIMELINE_SELECTOR)) {
    if (timeline.getAttribute(READY_ATTRIBUTE) === READY_VALUE) continue;
    if (initializeTimeline(timeline)) initialized += 1;
  }
  return initialized;
}
